import { Box, Tooltip } from '@chakra-ui/react'
import {
  Field,
  getFullName,
  InputSize,
  isConsentForm,
  TemplateFieldElement,
  TemplateNodeFieldMap,
} from '@hb/shared'
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react'
import { Path } from 'slate'
import { ReactEditor, RenderElementProps } from 'slate-react'
import { CustomRenderElementProps } from '../../../types/editor'
import { FormElement } from '../../forms/Input'
import { useNonEditable } from '../../Templates/useNonEditable'
import { useElectronicConsentOverlay } from '../../Users/Profile/ConsentForms/ElectronicConsent/context'
import { useDocumentForm } from '../context'
import { useElementStyle } from '../hooks'
import { useTemplateFieldSelection } from '../TemplateFieldSelectionContext'

const pathsEqual = (path1: Path, path2: Path) => {
  if (path1.length !== path2.length) return false
  for (let i = 0; i < path1.length; i += 1) {
    if (path1[i] !== path2[i]) return false
  }
  return true
}

export const TemplateFieldElementView = ({
  field,
  name,
  // mode,
  // isSelected,
  onSelect,
  style,
  size,
  inputStyle,
}: {
  field: Field
  name: string
  // mode: Mode
  // isSelected: boolean
  onSelect: (event: React.MouseEvent) => void
  style?: CSSProperties
  size?: InputSize
  inputStyle?: CSSProperties
}) => {
  const {
    fieldsDisabled: readOnly,
    signerLoading,
    toBeSignedByUser,
    toBeSignedByMe,
    troubleSigningOpen,
    document,
    termsOpen,
    registerInput,
    unregisterInput,
  } = useDocumentForm()

  useEffect(
    () => () => {
      unregisterInput(name)
    },
    [unregisterInput, name],
  )

  const isManuallySignedConsentForm = useMemo(
    () => document && isConsentForm(document) && document.signedOffSite === true,
    [document],
  )

  const { isOpen: consentModalOpen } = useElectronicConsentOverlay()

  const tooltip = useMemo(() => {
    if (!toBeSignedByMe) {
      return signerLoading
        ? 'Loading signer data...'
        : `To be entered by ${toBeSignedByUser ? getFullName(toBeSignedByUser) : 'ERROR: User not found'}`
    }
    return ''
  }, [signerLoading, toBeSignedByMe, toBeSignedByUser])

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const registerInputEl = useCallback(
    (el: HTMLInputElement) => {
      inputRef.current = el
      if (inputRef.current && containerRef.current) registerInput(name, el, containerRef.current)
    },
    [registerInput, name],
  )

  const registerContainerEl = useCallback(
    (el: HTMLDivElement) => {
      containerRef.current = el
      if (inputRef.current && containerRef.current)
        registerInput(name, inputRef.current, containerRef.current)
    },
    [registerInput, name],
  )

  // const borderColor = useMemo(() => {
  //   let bc = '#dedede'
  //   if (readOnly && error && toBeSignedByMe) bc = '#ff5555'
  //   else if (isSelected && mode === 'Edit') bc = colors.blue.hex
  //   return bc
  // }, [readOnly, error, isSelected, mode, toBeSignedByMe])
  return (
    <Box w="100%" h="100%" onClick={onSelect} ref={registerContainerEl} position="relative">
      <Tooltip hasArrow placement="top" label={tooltip}>
        <div
          style={{
            pointerEvents: toBeSignedByMe ? 'auto' : 'none',
          }}>
          <FormElement
            ref={registerInputEl}
            field={field}
            showErrorIfClean={!termsOpen && !consentModalOpen}
            readOnly={readOnly}
            disabled={!toBeSignedByMe || troubleSigningOpen}
            name={name}
            size={size}
            inputStyle={inputStyle}
            style={{
              // padding: '0.3rem 0.2rem',
              opacity: isManuallySignedConsentForm ? 0 : 1,
              backgroundColor: 'white',
              pointerEvents: isManuallySignedConsentForm ? 'none' : 'auto',
              ...style,
            }}
            // name={node.id}
          />
        </div>
      </Tooltip>
    </Box>
  )
}

const editorFieldStyle: CSSProperties = {
  padding: '0.4rem 0.35rem',
}

export const EditorFieldElementView = ({
  children,
  element,
  attributes,
  version,
  editor,
  mode,
}: RenderElementProps & CustomRenderElementProps) => {
  const node = element as TemplateFieldElement
  const {
    field: rootField,
    fieldsDisabled: readOnly,
    document,
  } = useDocumentForm<TemplateNodeFieldMap>()
  const style = useElementStyle(mode, node, version)

  const path = ReactEditor.findPath(editor, element)

  const { field, id } = useMemo(() => {
    const match = Object.entries(rootField?.children ?? {}).find(([_, f]) =>
      pathsEqual(f.path, path),
    )
    if (!match) return { field: null, id: '__NONE__' }
    return { field: match[1], id: match[0] }
  }, [rootField, path])

  const { onClick } = useNonEditable(editor, node, mode)
  const { selectedFieldId, setSelectedFieldId } = useTemplateFieldSelection()
  const isSelected = selectedFieldId === node.id

  const handleSelect = useCallback(
    (event: React.MouseEvent) => {
      // Field selection (and the `preventDefault()` inside `onClick`) is only for template
      // authoring — not consent-form/document filling (which also runs in 'Edit' mode but has a
      // `document`). During filling the click must reach the input untouched: calling
      // preventDefault() here cancels the browser's default toggle action for radio/checkbox
      // inputs, so selecting a dropdown option would never register.
      if (mode === 'Edit' && !document) {
        onClick(event)
        setSelectedFieldId(node.id)
      }
    },
    [onClick, mode, document, node.id, setSelectedFieldId],
  )

  return (
    <div
      {...attributes}
      contentEditable={false}
      data-field-node={node.id}
      style={{
        position: 'relative',
        cursor: readOnly ? 'default' : 'pointer',
        alignItems: 'center',
        userSelect: 'none',
        padding: '0.2rem 0',
        borderRadius: 4,
        boxShadow: isSelected ? '0 0 0 2px #3182ce' : undefined,
        ...style,
      }}>
      <TemplateFieldElementView
        onSelect={handleSelect}
        // // isSelected={isSelected}
        // mode={mode}
        field={field || node.field}
        name={id}
        style={editorFieldStyle}
      />
      <span
        style={{
          display: 'none',
        }}>
        {children}
      </span>
    </div>
  )
}
